import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';

const WEEKDAYS = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB'];

interface ReportCalendarProps {
  value?: Date;
  defaultMonth?: Date;
  annotatedDates?: Date[];
  selectableDates?: Date[];
  onSelect?: (date: Date) => void;
  annotationLabel?: React.ReactNode;
}

const sameDay = (a?: Date, b?: Date) =>
  !!a &&
  !!b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const cn = (...classes: (string | false | undefined)[]) =>
  classes.filter(Boolean).join(' ');

const ReportCalendar = ({
  value,
  defaultMonth,
  annotatedDates = [],
  selectableDates,
  onSelect,
  annotationLabel = 'Datas com anotações de campo',
}: ReportCalendarProps) => {
  const initial = value ?? defaultMonth ?? new Date();
  const [view, setView] = useState(
    new Date(initial.getFullYear(), initial.getMonth(), 1),
  );
  const today = new Date();

  const year = view.getFullYear();
  const month = view.getMonth();
  const firstWeekday = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevious = new Date(year, month, 0).getDate();

  const cells = Array.from({ length: 42 }, (_, index) => {
    const dayNumber = index - firstWeekday + 1;
    if (dayNumber < 1) {
      return {
        date: new Date(year, month - 1, daysInPrevious + dayNumber),
        outside: true,
      };
    }
    if (dayNumber > daysInMonth) {
      return {
        date: new Date(year, month + 1, dayNumber - daysInMonth),
        outside: true,
      };
    }
    return { date: new Date(year, month, dayNumber), outside: false };
  });

  const label = `${String(month + 1).padStart(2, '0')}/${year}`;

  return (
    <div
      className="w-72 rounded-aqila-lg border border-aqila-line bg-white p-4 shadow-aqila"
      data-slot="aqila-calendar"
    >
      <div className="flex items-center justify-between">
        <button
          aria-label="Mês anterior"
          className="flex size-8 items-center justify-center rounded-aqila-sm text-aqila-muted outline-none hover:bg-aqila-surface"
          type="button"
          onClick={() => setView(new Date(year, month - 1, 1))}
        >
          <ChevronLeft className="size-4" />
        </button>
        <span className="text-sm font-semibold text-aqila-body">{label}</span>
        <button
          aria-label="Próximo mês"
          className="flex size-8 items-center justify-center rounded-aqila-sm text-aqila-muted outline-none hover:bg-aqila-surface"
          type="button"
          onClick={() => setView(new Date(year, month + 1, 1))}
        >
          <ChevronRight className="size-4" />
        </button>
      </div>

      <div className="mt-3 grid grid-cols-7 gap-1 text-center">
        {WEEKDAYS.map((weekday) => (
          <span
            key={weekday}
            className="py-1 text-xs font-medium text-aqila-muted"
          >
            {weekday}
          </span>
        ))}
        {cells.map(({ date, outside }) => {
          const selected = sameDay(date, value);
          const isToday = sameDay(date, today);
          const annotated = annotatedDates.some((item) => sameDay(item, date));
          const disabled = Boolean(
            selectableDates &&
              !selectableDates.some((item) => sameDay(item, date)),
          );

          return (
            <button
              key={date.getTime()}
              className={cn(
                'relative mx-auto flex size-9 items-center justify-center rounded-full text-sm outline-none',
                outside ? 'text-aqila-hint' : 'text-aqila-body',
                selected && 'bg-aqila-primary text-white',
                !selected && isToday && 'ring-1 ring-aqila-primary',
              )}
              disabled={disabled}
              style={
                disabled ? { opacity: 0.3, cursor: 'not-allowed' } : undefined
              }
              type="button"
              onClick={() => onSelect?.(date)}
            >
              {date.getDate()}
              {annotated && !selected ? (
                <span className="absolute bottom-1 size-1 rounded-full bg-aqila-primary" />
              ) : null}
            </button>
          );
        })}
      </div>

      <div className="mt-3 flex items-center gap-2 text-xs text-aqila-muted">
        <span className="size-2 rounded-full bg-aqila-primary" />
        {annotationLabel}
      </div>
    </div>
  );
};

export default ReportCalendar;
